import { generateNthOptions } from "functions";
import dayjs from "dayjs";
import { Operation, OperationGroup, Task } from "types";
import { currentTimeFormat } from "us.common/constants";

export enum DrawerType {
  NONE = "",
  NEW_GROUP = "NEW_GROUP",
  EDIT_GROUP = "EDIT_GROUP",
  NEW_TASK = "NEW_TASK",
  EDIT_TASK = "EDIT_TASK",
  NEW_OPERATION = "NEW_OPERATION",
  EDIT_OPERATION = "EDIT_OPERATION",
}

export const DEFAULT_DRAWER_STATE = {
  visible: false,
  title: "",
  type: DrawerType.NONE,
  data: null,
};

export enum TaskStatus {
  PENDING = "Pending",
  PENDING_AND_DUE = "Pending and Due",
  DONE = "Done",
  HANDLED = "Handled",
  FAILED = "Failed",
  SYSTEM_ERROR = "System Error",
  NOT_SCHEDULED = "Not Scheduled",
}

export const InstanceStatusOptions = [
  { label: "DONE", value: TaskStatus.DONE },
  { label: "NOT_DONE", value: TaskStatus.PENDING },
  {
    label: "DEVIATION_NOT_HANDLED",
    value: TaskStatus.FAILED,
  },
  { label: "DEVIATION_HANDLED", value: TaskStatus.HANDLED },
];

export enum DashboardAction {
  NEW_GROUP = "NEW_GROUP",
  EDIT_GROUP = "EDIT_GROUP",
  DELETE_GROUP = "DELETE_GROUP",
  NEW_TASK = "NEW_TASK",
  EDIT_TASK = "EDIT_TASK",
  NEW_OPERATION = "NEW_OPERATION",
  EDIT_OPERATION = "EDIT_OPERATION",
  RUN_TASK = "RUN_TASK",
  VIEW_OPERATION = "VIEW_OPERATION",
}

export enum OperationDetailsTabs {
  GENERAL_INFO = "GENERAL_INFO",
  SEND_EMAIL = "SEND_EMAIL",
  TASK_DETAILS = "TASK_DETAILS",
  ERROR_HANDLING = "ERROR_HANDLING",
  TASK_HISTORY = "TASK_HISTORY",
}

export enum ScheduleType {
  CUSTOM = "Custom",
  DAILY = "Daily",
  WEEKLY = "Weekly",
  MONTHLY = "Monthly",
  YEARLY = "Yearly",
}

export enum SubScheduleType {
  EVERY_N_DAY = "every-n-day",
  EVERY_WEEK_DAY = "every-week-day",
  EVERY_N_MONTH_WITH_NTH_DAY = "every-n-month-with-nth-day",
  EVERY_N_MONTH_WITH_SPECIFIC_DAY = "every-n-month-with-specific-day",
  EVERY_N_MONTH_WITH_NTH_DAY_YEARLY = "every-n-month-with-nth-day-yearly",
  EVERY_N_MONTH_WITH_SPECIFIC_DAY_YEARLY = "every-n-month-with-specific-day-yearly",
}

export enum DayName {
  SUNDAY = "Sunday",
  MONDAY = "Monday",
  TUESDAY = "Tuesday",
  WEDNESDAY = "Wednesday",
  THURSDAY = "Thursday",
  FRIDAY = "Friday",
  SATURDAY = "Saturday",
}

export const DayMap: Record<string, string> = {
  "1": DayName.MONDAY,
  "2": DayName.TUESDAY,
  "3": DayName.WEDNESDAY,
  "4": DayName.THURSDAY,
  "5": DayName.FRIDAY,
  "6": DayName.SATURDAY,
  "7": DayName.SUNDAY,
};

export enum MonthName {
  JANUARY = "January",
  FEBRUARY = "February",
  MARCH = "March",
  APRIL = "April",
  MAY = "May",
  JUNE = "June",
  JULY = "July",
  AUGUST = "August",
  SEPTEMBER = "September",
  OCTOBER = "October",
  NOVEMBER = "November",
  DECEMBER = "December",
}

export const MonthMap: Record<string, string> = {
  "1": MonthName.JANUARY,
  "2": MonthName.FEBRUARY,
  "3": MonthName.MARCH,
  "4": MonthName.APRIL,
  "5": MonthName.MAY,
  "6": MonthName.JUNE,
  "7": MonthName.JULY,
  "8": MonthName.AUGUST,
  "9": MonthName.SEPTEMBER,
  "10": MonthName.OCTOBER,
  "11": MonthName.NOVEMBER,
  "12": MonthName.DECEMBER,
};

export const DEFAULT_GROUP_DETAILS: OperationGroup = {
  groupId: -1,
  groupName: "",
};

export const DEFAULT_OPERATION_DETAILS: Operation = {
  taskId: 0,
  groupId: 0,
  title: "",
  description: "",
  errorHandlingText: "",
  primaryAssignee: {
    id: "",
    email: "",
    name: "",
  },
  secondaryAssignee: {
    id: "",
    email: "",
    name: "",
  },
  isAutoRun: false,
  schedule: "",
  startDate: "",
  endDate: "",
  createdTime: "",
  createdBy: "",
  parameters: [],
  status: "",
  timeSpend: "",
  comment: "",
  executedTime: "",
  executedBy: "",
  scheduledTime: "",
  primaryCustomer: "",
  secondaryCustomer: "",
  groupName: "",
  isInternalChecking: false,
  taskInstanceId: 0,
  executionScript: "",
  executionType: "",
  emailText: "",
  modifiedTime: "",
};

export const DEFAULT_TASK_DETAILS: Task = {
  taskId: -1,
  title: "",
  description: "",
  errorHandlingText: "",
  primaryCustomer: "",
  secondaryCustomer: "",
  primaryAssignee: "",
  secondaryAssignee: "",
  isAutoRun: false,
  schedule: "",
  startDate: dayjs().format(currentTimeFormat),
  endDate: "",
  groupId: 0,
  executionScript: "",
  executionType: "SP",
  emailText: "",
  isInternalChecking: false,
};

export const DEFAULT_TASK_INSTANCE = {
  taskInstanceId: -1,
  status: TaskStatus.PENDING,
  timeSpend: "",
  comment: "",
};

export const DAYS_OPTIONS = Array.from({ length: 7 }, (_, i) => ({
  label: DayMap[i + 1],
  value: i + 1,
}));

export const MONTHS_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  label: MonthMap[i + 1],
  value: i + 1,
}));

export const NTH_DAY_NAME_OPTIONS = Array.from({ length: 5 }, (_, i) => ({
  label: ["First", "Second", "Third", "Fourth", "Fifth"][i],
  value: i + 1,
}));

export const NTH_DAY_OPTIONS = generateNthOptions(31, "day");

export const N_MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  label: i + 1,
  value: i + 1,
}));
